import { useEffect, useState } from 'react'
import { Navigate, NavLink, Outlet, useNavigate } from 'react-router'
import { ApiClientError } from '@olmt/shared'
import { api, describeError, type SessionUser } from '../lib/api'
import { signOut } from '../lib/session'
import { useAuthStore } from '../stores/authStore'
import { AccountGateNotice } from '../components/AccountGateNotice'
import { AuthNotice } from '../components/AuthNotice'
import { LogOutIcon, ShieldIcon } from '../components/Icons'
import { Spinner } from '../components/Spinner'
import { StatsStrip } from '../components/StatsStrip'

type Probe =
  | { state: 'checking'; user: SessionUser | null }
  | { state: 'ready'; user: SessionUser }
  | { state: 'failed'; message: string }

const NAV_ITEMS = [
  { to: '/queue', label: 'Queue' },
  { to: '/users', label: 'Users' },
  { to: '/invites', label: 'Invites' },
]

/**
 * The console's door. A persisted session is only a claim; `GET /api/me` is the fact (§11 says it
 * always answers, whatever the caller's status), so every mount re-reads it and the whole console
 * branches on the answer: not approved → the waiting screen, approved but not an administrator →
 * the refusal. While the answer is not `approved` nothing else is called.
 */
export function ConsoleLayout() {
  const navigate = useNavigate()
  const token = useAuthStore((state) => state.token)
  const setUser = useAuthStore((state) => state.setUser)
  const [probe, setProbe] = useState<Probe>({ state: 'checking', user: null })
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    if (!token) return
    let active = true
    setProbe((previous) => ({ state: 'checking', user: previous.state === 'ready' ? previous.user : null }))
    api.me().then(
      (response) => {
        if (!active) return
        setUser(response.user)
        setProbe({ state: 'ready', user: response.user })
      },
      (caught: unknown) => {
        if (!active) return
        // A token the api no longer knows is not an error screen: it is a signed-out console.
        if (caught instanceof ApiClientError && caught.code === 'unauthorized') {
          signOut()
          return
        }
        setProbe({ state: 'failed', message: describeError(caught, 'Could not check this account.') })
      },
    )
    return () => {
      active = false
    }
  }, [token, nonce, setUser])

  const leave = () => {
    signOut()
    void navigate('/login', { replace: true })
  }

  if (!token) return <Navigate to="/login" replace />

  if (probe.state === 'checking') {
    return (
      <div className="flex min-h-dvh items-center justify-center px-6">
        <span className="flex items-center gap-2 text-ui text-muted">
          <Spinner size={18} label="Checking this account" />
          Checking this account…
        </span>
      </div>
    )
  }

  if (probe.state === 'failed') {
    return (
      <div className="flex min-h-dvh items-center justify-center px-6">
        <div className="w-full max-w-md">
          <AuthNotice
            tone="danger"
            title="Could not check this account"
            actions={
              <>
                <button
                  type="button"
                  onClick={() => setNonce((value) => value + 1)}
                  className="press min-h-11 rounded-full bg-accent px-5 text-ui font-semibold text-on-accent hover:bg-accent-strong"
                >
                  Try again
                </button>
                <button
                  type="button"
                  onClick={leave}
                  className="press min-h-11 rounded-full px-5 text-ui font-medium text-ink ring-1 ring-line ring-inset hover:bg-surface2"
                >
                  Sign out
                </button>
              </>
            }
          >
            <p>{probe.message}</p>
          </AuthNotice>
        </div>
      </div>
    )
  }

  const user = probe.user
  if (user.status !== 'approved' || !user.isAdmin) {
    return (
      <div className="flex min-h-dvh items-center justify-center px-6">
        <div className="w-full max-w-md">
          <AccountGateNotice
            user={user}
            actions={[
              { label: 'Check again', onClick: () => setNonce((value) => value + 1), primary: true },
              { label: 'Sign out', onClick: leave },
            ]}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="app-shell">
      <header className="app-header border-b border-line bg-surface">
        <div className="console-column flex flex-wrap items-center gap-x-3 gap-y-2 py-2">
          <span
            aria-hidden="true"
            className="flex size-8 items-center justify-center rounded-full bg-accent/12 ring-1 ring-accent/30 ring-inset"
          >
            <span className="size-2.5 rounded-full bg-accent shadow-[0_0_12px_2px_rgb(232_176_75/0.45)]" />
          </span>
          <h1 className="text-title font-semibold tracking-tight text-ink">
            olmt<span className="text-accent">·</span>call
            <span className="ms-2 align-middle text-meta font-medium text-muted">console</span>
          </h1>

          <nav aria-label="Console sections" className="flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `press flex min-h-11 items-center rounded-full px-3.5 text-ui ${
                    isActive
                      ? 'bg-accent/12 font-semibold text-accent ring-1 ring-accent/30 ring-inset'
                      : 'text-muted hover:bg-surface2 hover:text-ink'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <span className="ms-auto flex items-center gap-1.5">
            <ShieldIcon size={16} className="text-accent" />
            <span className="max-w-40 truncate text-ui text-ink">
              <bdi>{user.displayName}</bdi>
            </span>
            <button
              type="button"
              onClick={leave}
              className="press flex min-h-11 items-center gap-2 rounded-full px-3 text-ui text-muted hover:bg-surface2 hover:text-ink"
            >
              <LogOutIcon size={16} />
              Sign out
            </button>
          </span>
        </div>
        <div className="console-column border-t border-line/70 py-1">
          <StatsStrip />
        </div>
      </header>

      <main className="scroll-area">
        <div className="console-column px-4 py-5">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
